import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Save, ShieldCheck, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, SectionTitle } from '@/components/design-system';
import { useUpdateUserMeta, useUsersList } from '@/hooks/queries/useUsersList';
import { FEATURE_CATALOG, PERMISSIONS_TEMPLATES, type FeatureKey } from '@/lib/permissions';
import { ROLE_LABELS } from '@/lib/auth';

// Sprint 8.1 — Édition des permissions par feature pour un user.
// Whitelist explicite : si permissions != null, seules les keys true sont autorisées.

type Mode = 'inherit' | 'custom';

export function UserPermissionsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { data: users = [] } = useUsersList();
  const updateMeta = useUpdateUserMeta();

  const user = users.find((u) => u.user_id === id) ?? null;

  const [mode, setMode] = useState<Mode>('inherit');
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!user) return;
    const existing =
      (user as unknown as { permissions?: Record<string, boolean> | null }).permissions ?? null;
    if (existing == null) {
      setMode('inherit');
      setPermissions({});
    } else {
      setMode('custom');
      setPermissions(existing);
    }
  }, [user]);

  const totalEnabled = useMemo(
    () => Object.values(permissions).filter(Boolean).length,
    [permissions],
  );

  const handleToggle = (key: FeatureKey): void => {
    setPermissions((cur) => ({ ...cur, [key]: !cur[key] }));
  };

  const handleApplyTemplate = (templateId: string): void => {
    const tpl = PERMISSIONS_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    setMode('custom');
    setPermissions({ ...tpl.permissions } as Record<string, boolean>);
  };

  const handleSave = async (): Promise<void> => {
    if (!user) return;
    try {
      await updateMeta.mutateAsync({
        user_id: user.user_id,
        permissions: mode === 'inherit' ? null : permissions,
      });
      toast.success('Permissions mises à jour');
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  if (!user) {
    return (
      <PageContainer>
        <Link to="/parametres/utilisateurs" className="text-xs hover:underline">
          <ChevronLeft className="inline h-3 w-3" /> Retour
        </Link>
        <div className="mt-4 rounded-md border border-slate-200 bg-white p-6 text-sm">
          Utilisateur introuvable.
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Link
        to="/parametres/utilisateurs"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline"
      >
        <ChevronLeft className="h-3 w-3" /> Retour aux utilisateurs
      </Link>

      <header className="mt-3 rounded-xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
              <ShieldCheck className="h-6 w-6 text-emerald-600" /> Permissions
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {user.full_name ?? user.user_id.slice(0, 8)} ·{' '}
              <Badge variant="outline" className="text-[10px]">
                {ROLE_LABELS[user.role]}
              </Badge>
            </p>
          </div>
          <Button onClick={handleSave} disabled={updateMeta.isPending}>
            <Save className="h-3.5 w-3.5" /> Enregistrer
          </Button>
        </div>
      </header>

      {/* Mode toggle */}
      <section className="mt-5 rounded-xl border border-slate-200 bg-white p-4">
        <SectionTitle>Mode d&apos;autorisation</SectionTitle>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setMode('inherit')}
            className={
              'rounded-lg border p-3 text-left text-xs transition-colors ' +
              (mode === 'inherit'
                ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300'
                : 'border-slate-200 bg-white hover:border-slate-300')
            }
          >
            <div className="font-semibold">Hériter du rôle</div>
            <div className="mt-0.5 text-muted-foreground">
              Permissions par défaut du rôle <strong>{ROLE_LABELS[user.role]}</strong>. Compatible
              avec l&apos;existant.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode('custom')}
            className={
              'rounded-lg border p-3 text-left text-xs transition-colors ' +
              (mode === 'custom'
                ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-300'
                : 'border-slate-200 bg-white hover:border-slate-300')
            }
          >
            <div className="font-semibold">Permissions personnalisées</div>
            <div className="mt-0.5 text-muted-foreground">
              Whitelist stricte : l&apos;user n&apos;a accès qu&apos;aux features cochées
              ci-dessous.
            </div>
          </button>
        </div>
      </section>

      {mode === 'custom' && (
        <>
          {/* Templates */}
          <section className="mt-4 rounded-xl border border-violet-200 bg-violet-50/40 p-4">
            <SectionTitle>
              <span className="inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-violet-600" /> Templates rapides
              </span>
            </SectionTitle>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {PERMISSIONS_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  onClick={() => handleApplyTemplate(tpl.id)}
                  className="rounded-lg border border-violet-200 bg-white p-3 text-left text-xs hover:border-violet-400 hover:bg-violet-50/50"
                >
                  <div className="font-semibold">{tpl.label}</div>
                  <div className="mt-0.5 text-muted-foreground">{tpl.description}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Catalog par module */}
          <section className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <SectionTitle>Permissions accordées</SectionTitle>
              <span className="text-xs text-muted-foreground">
                {totalEnabled} feature{totalEnabled > 1 ? 's' : ''} cochée
                {totalEnabled > 1 ? 's' : ''}
              </span>
            </div>

            {FEATURE_CATALOG.map((group) => (
              <div key={group.module} className="rounded-xl border border-slate-200 bg-white p-4">
                <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-700">
                  {group.label}
                </h3>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {group.features.map((f) => (
                    <label
                      key={f.key}
                      className="flex items-start gap-2 rounded-md border border-slate-100 bg-slate-50/40 px-2.5 py-1.5 text-xs hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={permissions[f.key] === true}
                        onChange={() => handleToggle(f.key)}
                        className="mt-0.5 h-3.5 w-3.5"
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="font-medium">{f.label}</span>
                        <span className="font-mono text-[9px] text-slate-400">{f.key}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
    </PageContainer>
  );
}
